import React, { useEffect, useState } from 'react';
import styled, { ThemeProvider, css } from 'styled-components';
import { injectIntl } from 'react-intl';
import { IntlShape } from 'react-intl';
import SessionInstanceFactory from './session-instance';
import {withState, Icons} from '@kepler.gl/components';
import {loadSessionMap} from '../../actions';

import {
  LoadingDialog
} from '@kepler.gl/components';

import axios from 'axios';

const host = process.env.API_URL;

const StyledModal = styled.div`
  min-height: 50vh;
  overflow: hidden;
  display: flex;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;

  th,
  td {
    padding: 8px;
    text-align: left;
  }
`;

const StyledNewMapRow = styled.tr`
  background-color: lightgreen; /* Set the background color to green */
  font-weight: bold;
  &:hover {
    background-color: green;
    cursor: pointer;
    color: white; /* Set the text color to white for better visibility */
  }
`;

const StyledTableCell = styled.td`
  padding: 8px;
  text-align: left; /* Left justify text */
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledPlayCell = styled.td`
  padding: 8px; /* Adjust padding as needed */
  width: auto; /* Set width to auto to fit the play button */
  &:hover {
    font-weight: bold;
    background-color: lightgreen;
    cursor: pointer;
  }
`;

const StyledTableRow = styled.tr`
  &:nth-child(odd) {
    background-color: #f4f4f4; /* Shading for alternate rows */
  }
  &:hover > :not(:last-child) {
    font-weight: bold;
    background-color: lightgray;
    cursor: pointer;
  }
`;

export const StyledInputLabel = styled.label`
  font-size: 12px;
  color: ${props => props.theme.textColorLT};
  letter-spacing: 0.2px;
`;

const StyledInnerDiv = styled.div`
  min-height: 500px;
  width: 100%;
`;

interface SessionManagerFactoryProps {
  onGenerateSessionData: () => {config: any; datasets: string[]};
}

export interface Session {
  id: string;
  name: string;
  share_type: string;
  password: string;
  creation_time: string;
  data_files: string[];
  config_id: string;
}

SessionManagerFactory.deps = [SessionInstanceFactory]

export default function SessionManagerFactory(
  SessionInstance: ReturnType<typeof SessionInstanceFactory>,
) {
  const SessionInstanceModal: React.FC<SessionManagerFactoryProps & IntlProps> = ({
    onGenerateSessionData,
    onLoadSession,
  }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [selectedSession, setSelectedSession] = useState<Session>({} as Session);
    const [newSession, setNewSession] = useState(false);
    const [url, setUrl] = useState('');

    useEffect(() => {
      fetchSessions();
    }, []);

    const fetchSessions = async () => {
      setIsLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const response = await axios.get(`${host}/api/sessions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        const sessions = response.data.body.sessions
        setSessions(sessions);
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const convertTimestamp = (timestamp) => {
      if (timestamp) {
        const utcDate = new Date(timestamp);
        return utcDate.toLocaleString();
      } else {
        return ""
      }
    };

    // TODO: Fix URL
    const handleMapClick = (session) => {
      setSelectedSession(session);
      setUrl(`${host}/sessions/${session['id']}`);
    }

    const handleBackButtonClick = () => {
      fetchSessions();
      setNewSession(false);
      setSelectedSession({} as Session);
    }

    const handleSaveButtonClick = async () => {
      await fetchSessions();
      setNewSession(false);
      setSelectedSession({} as Session);
    }

    const handleNewButtonClick = () => {
      setUrl('');
      setNewSession(true);
    }

    const loadSession = (session) => {
      setIsLoading(true);
      onLoadSession({
        dataUrl: `${host}/api/sessions/${session.id}/map`,
        data: {
          password: session.password
        }
      })
    }

    return (
      <StyledModal className="dataset-modal" id="dataset-modal">
        {(isLoading) ? 
          <LoadingDialog size={64} message="Retrieving sessions..."/>
        : 
        (selectedSession && selectedSession.id || newSession )? (
          <div>
            <SessionInstance onGenerateSessionData={onGenerateSessionData} session={selectedSession} newSession={newSession} mapUrl={url} onBack={handleBackButtonClick} onSave={handleSaveButtonClick}/>
          </div>
        ) : (
          <StyledInnerDiv>
            <div>
              Click each row to edit existing saved sessions. Click the play button to load the session now.
            </div>
            <StyledTable>
              <thead>
                <tr>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>ID</StyledTableCell>
                  <StyledTableCell>Creation Date</StyledTableCell>
                </tr>
              </thead>
              <tbody>
                <StyledNewMapRow  key="new-session" onClick={() => handleNewButtonClick()}>
                  <StyledTableCell colSpan={3}>+ Save current session</StyledTableCell>
                </StyledNewMapRow>
              {sessions.map(session => (
                <StyledTableRow key={session.id}>
                  <StyledTableCell onClick={() => handleMapClick(session)}>{session.name}</StyledTableCell>
                  <StyledTableCell onClick={() => handleMapClick(session)}>{session.id}</StyledTableCell>
                  <StyledTableCell onClick={() => handleMapClick(session)}>{convertTimestamp(session.creation_time)}</StyledTableCell>
                  <StyledPlayCell onClick={() => loadSession(session)}>
                    <Icons.Play/>
                  </StyledPlayCell>
                </StyledTableRow>
              ))}
              </tbody>
            </StyledTable>
          </StyledInnerDiv>
        )}
      </StyledModal>
    );
  };

  return withState([], state => ({}), {onLoadSession: loadSessionMap})(injectIntl(SessionInstanceModal));
}
